"use strict";

/**
  * @reach/accordion v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */

// src/reach-accordion.tsx
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["as", "children", "defaultIndex", "index", "onChange", "readOnly", "collapsible", "multiple"],
  _excluded2 = ["as", "children", "disabled", "index"],
  _excluded3 = ["as", "children", "onClick", "onKeyDown", "onMouseDown", "onPointerDown", "tabIndex"],
  _excluded4 = ["as", "children"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as React from "react";
import { createContext, makeId, noop, useComposedRefs, useControlledState, composeEventHandlers, useStatefulRefValue } from "@reach/utils";
import { createDescendantContext, DescendantProvider, useDescendant, useDescendantKeyDown, useDescendantsInit } from "@reach/descendants";
import { useId } from "@reach/auto-id";
var AccordionDescendantContext = createDescendantContext("AccordionDescendantContext");
var _createContext = createContext("Accordion"),
  _createContext2 = _slicedToArray(_createContext, 2),
  AccordionProvider = _createContext2[0],
  useAccordionCtx = _createContext2[1];
var _createContext3 = createContext("AccordionItem"),
  _createContext4 = _slicedToArray(_createContext3, 2),
  AccordionItemProvider = _createContext4[0],
  useAccordionItemCtx = _createContext4[1];
var AccordionStates = /* @__PURE__ */function (AccordionStates2) {
  AccordionStates2["Open"] = "OPEN";
  AccordionStates2["Collapsed"] = "COLLAPSED";
  return AccordionStates2;
}(AccordionStates || {});
var Accordion = React.forwardRef(function Accordion2(_ref, forwardedRef) {
  var _ref$as = _ref.as,
    Comp = _ref$as === void 0 ? "div" : _ref$as,
    children = _ref.children,
    defaultIndex = _ref.defaultIndex,
    controlledIndex = _ref.index,
    onChange = _ref.onChange,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    _ref$collapsible = _ref.collapsible,
    collapsible = _ref$collapsible === void 0 ? false : _ref$collapsible,
    _ref$multiple = _ref.multiple,
    multiple = _ref$multiple === void 0 ? false : _ref$multiple,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useControlledState = useControlledState({
      controlledValue: controlledIndex,
      defaultValue: function defaultValue() {
        if (defaultIndex != null) {
          if (multiple) {
            return Array.isArray(defaultIndex) ? defaultIndex : [defaultIndex];
          } else {
            var _defaultIndex$;
            return Array.isArray(defaultIndex) ? (_defaultIndex$ = defaultIndex[0]) !== null && _defaultIndex$ !== void 0 ? _defaultIndex$ : 0 : defaultIndex;
          }
        }
        if (collapsible) {
          return multiple ? [] : -1;
        }
        return multiple ? [0] : 0;
      },
      calledFrom: "Tabs"
    }),
    _useControlledState2 = _slicedToArray(_useControlledState, 2),
    openPanels = _useControlledState2[0],
    setOpenPanels = _useControlledState2[1];
  var _useDescendantsInit = useDescendantsInit(),
    _useDescendantsInit2 = _slicedToArray(_useDescendantsInit, 2),
    descendants = _useDescendantsInit2[0],
    setDescendants = _useDescendantsInit2[1];
  var id = useId(props.id);
  var onSelectPanel = React.useCallback(function (index) {
    onChange && onChange(index);
    setOpenPanels(function (prevOpenPanels) {
      index = index;
      if (multiple) {
        prevOpenPanels = prevOpenPanels;
        if (prevOpenPanels.includes(index)) {
          if (prevOpenPanels.length > 1 || collapsible) {
            return prevOpenPanels.filter(function (i) {
              return i !== index;
            });
          }
        } else {
          return [].concat(_toConsumableArray(prevOpenPanels), [index]).sort();
        }
      } else {
        prevOpenPanels = prevOpenPanels;
        return prevOpenPanels === index && collapsible ? -1 : index;
      }
      return prevOpenPanels;
    });
  }, [collapsible, multiple, onChange, setOpenPanels]);
  return /* @__PURE__ */React.createElement(DescendantProvider, {
    context: AccordionDescendantContext,
    items: descendants,
    set: setDescendants
  }, /* @__PURE__ */React.createElement(AccordionProvider, {
    accordionId: id,
    openPanels: openPanels,
    onSelectPanel: readOnly ? noop : onSelectPanel,
    readOnly: readOnly
  }, /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    "data-reach-accordion": ""
  }), children)));
});
Accordion.displayName = "Accordion";
var AccordionItem = React.forwardRef(function AccordionItem2(_ref2, forwardedRef) {
  var _ref2$as = _ref2.as,
    Comp = _ref2$as === void 0 ? "div" : _ref2$as,
    children = _ref2.children,
    _ref2$disabled = _ref2.disabled,
    disabled = _ref2$disabled === void 0 ? false : _ref2$disabled,
    indexProp = _ref2.index,
    props = _objectWithoutProperties(_ref2, _excluded2);
  var _useAccordionCtx = useAccordionCtx("AccordionItem"),
    accordionId = _useAccordionCtx.accordionId,
    openPanels = _useAccordionCtx.openPanels,
    readOnly = _useAccordionCtx.readOnly;
  var buttonRef = React.useRef(null);
  var _useStatefulRefValue = useStatefulRefValue(buttonRef, null),
    _useStatefulRefValue2 = _slicedToArray(_useStatefulRefValue, 2),
    element = _useStatefulRefValue2[0],
    handleButtonRefSet = _useStatefulRefValue2[1];
  var descendant = React.useMemo(function () {
    return {
      element: element,
      disabled: disabled
    };
  }, [disabled, element]);
  var index = useDescendant(descendant, AccordionDescendantContext, indexProp);
  var itemId = makeId(accordionId, index);
  var panelId = makeId("panel", itemId);
  var buttonId = makeId("button", itemId);
  var state = (Array.isArray(openPanels) ? openPanels.includes(index) && "OPEN" /* Open */ : openPanels === index && "OPEN" /* Open */) || "COLLAPSED" /* Collapsed */;
  var context = {
    buttonId: buttonId,
    buttonRef: buttonRef,
    disabled: disabled,
    handleButtonRefSet: handleButtonRefSet,
    index: index,
    itemId: itemId,
    panelId: panelId,
    state: state
  };
  return /* @__PURE__ */React.createElement(AccordionItemProvider, _objectSpread({}, context), /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    "data-reach-accordion-item": "",
    "data-state": getDataState(state),
    "data-disabled": disabled ? "" : void 0,
    "data-read-only": readOnly ? "" : void 0
  }), children));
});
AccordionItem.displayName = "AccordionItem";
var AccordionButton = React.forwardRef(function AccordionButton2(_ref3, forwardedRef) {
  var _ref3$as = _ref3.as,
    Comp = _ref3$as === void 0 ? "button" : _ref3$as,
    children = _ref3.children,
    onClick = _ref3.onClick,
    onKeyDown = _ref3.onKeyDown,
    onMouseDown = _ref3.onMouseDown,
    onPointerDown = _ref3.onPointerDown,
    tabIndex = _ref3.tabIndex,
    props = _objectWithoutProperties(_ref3, _excluded3);
  var _useAccordionCtx2 = useAccordionCtx("AccordionButton"),
    onSelectPanel = _useAccordionCtx2.onSelectPanel;
  var _useAccordionItemCtx = useAccordionItemCtx("AccordionButton"),
    disabled = _useAccordionItemCtx.disabled,
    buttonId = _useAccordionItemCtx.buttonId,
    ownRef = _useAccordionItemCtx.buttonRef,
    handleButtonRefSet = _useAccordionItemCtx.handleButtonRefSet,
    index = _useAccordionItemCtx.index,
    panelId = _useAccordionItemCtx.panelId,
    state = _useAccordionItemCtx.state;
  var ref = useComposedRefs(forwardedRef, handleButtonRefSet);
  function handleClick(event) {
    event.preventDefault();
    if (disabled) {
      return;
    }
    ownRef.current.focus();
    onSelectPanel(index);
  }
  var handleKeyDown = useDescendantKeyDown(AccordionDescendantContext, {
    currentIndex: index,
    orientation: "vertical",
    key: "element",
    rotate: true,
    callback: function callback(element) {
      element === null || element === void 0 ? void 0 : element.focus();
    },
    filter: function filter(button) {
      return !button.disabled;
    }
  });
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    "aria-controls": panelId,
    "aria-expanded": state === "OPEN" /* Open */,
    tabIndex: disabled ? -1 : tabIndex
  }, props), {}, {
    ref: ref,
    "data-reach-accordion-button": "",
    "data-state": getDataState(state),
    disabled: disabled || void 0,
    id: buttonId,
    onClick: composeEventHandlers(onClick, handleClick),
    onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown)
  }), children);
});
AccordionButton.displayName = "AccordionButton";
var AccordionPanel = React.forwardRef(function AccordionPanel2(_ref4, forwardedRef) {
  var _ref4$as = _ref4.as,
    Comp = _ref4$as === void 0 ? "div" : _ref4$as,
    children = _ref4.children,
    props = _objectWithoutProperties(_ref4, _excluded4);
  var _useAccordionItemCtx2 = useAccordionItemCtx("AccordionPanel"),
    disabled = _useAccordionItemCtx2.disabled,
    panelId = _useAccordionItemCtx2.panelId,
    buttonId = _useAccordionItemCtx2.buttonId,
    state = _useAccordionItemCtx2.state;
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    hidden: state !== "OPEN" /* Open */,
    role: "region",
    "aria-labelledby": buttonId
  }, props), {}, {
    ref: forwardedRef,
    "data-reach-accordion-panel": "",
    "data-disabled": disabled || void 0,
    "data-state": getDataState(state),
    id: panelId
  }), children);
});
AccordionPanel.displayName = "AccordionPanel";
function useAccordionContext() {
  var _useAccordionCtx3 = useAccordionCtx("useAccordionContext"),
    openPanels = _useAccordionCtx3.openPanels,
    accordionId = _useAccordionCtx3.accordionId;
  return React.useMemo(function () {
    var panels = [];
    return {
      id: accordionId,
      openPanels: panels.concat(openPanels).filter(function (i) {
        return i >= 0;
      })
    };
  }, [accordionId, openPanels]);
}
function useAccordionItemContext() {
  var _useAccordionItemCtx3 = useAccordionItemCtx("useAccordionItemContext"),
    index = _useAccordionItemCtx3.index,
    state = _useAccordionItemCtx3.state;
  return React.useMemo(function () {
    return {
      index: index,
      isExpanded: state === "OPEN" /* Open */
    };
  }, [index, state]);
}
function getDataState(state) {
  return state === "OPEN" /* Open */ ? "open" : "collapsed";
}
export { Accordion, AccordionButton, AccordionItem, AccordionPanel, AccordionStates, useAccordionContext, useAccordionItemContext };